import { nonNullable } from "@utils/utils";
import { useEffect, useReducer } from "react";

import { EhrGoal, EhrTarget, GetReassessmentEhrGoalsQuery } from "@api/graphql/types-and-hooks";

enum SelectProgrammingActionKind {
  INITIALIZE = "INITIALIZE",
  TOGGLE_GOAL = "TOGGLE_GOAL",
  TOGGLE_TARGET = "TOGGLE_TARGET",
  TOGGLE_EXPAND_GOAL = "TOGGLE_EXPAND_GOAL",
}

type SelectProgrammingAction =
  | { type: SelectProgrammingActionKind.INITIALIZE; goals: EhrGoal[] }
  | { type: SelectProgrammingActionKind.TOGGLE_GOAL; payload: string[]; checked: boolean; cascadeTargets?: boolean }
  | { type: SelectProgrammingActionKind.TOGGLE_TARGET; payload: string }
  | { type: SelectProgrammingActionKind.TOGGLE_EXPAND_GOAL; payload: string };

export type SelectProgrammingState = GoalToggleState[];

export type GoalToggleState = Omit<EhrGoal, "ehrTargets"> & {
  expanded: boolean;
  checked: boolean;
  disabled: boolean;
  targets: TargetToggleState[];
};

export type TargetToggleState = EhrTarget & {
  checked: boolean;
  disabled: boolean;
};

function reducer(state: SelectProgrammingState, action: SelectProgrammingAction) {
  switch (action.type) {
    case SelectProgrammingActionKind.TOGGLE_GOAL: {
      const ids = new Set(action.payload);
      return state.map((goal) => {
        if (!ids.has(goal.goalId) || goal.disabled) return goal;
        // Cascade only when opted in and the goal flips unchecked -> checked;
        // otherwise keep target selections.
        const cascade = action.cascadeTargets && action.checked && !goal.checked;
        return {
          ...goal,
          checked: action.checked,
          targets: goal.targets.map((target) => ({
            ...target,
            checked: cascade ? true : target.checked,
            disabled: !action.checked,
          })),
        };
      });
    }
    case SelectProgrammingActionKind.TOGGLE_EXPAND_GOAL:
      return state.map((goal) => {
        if (goal.goalId !== action.payload) return goal;
        return { ...goal, expanded: !goal.expanded };
      });
    case SelectProgrammingActionKind.TOGGLE_TARGET:
      return state.map((goal) => {
        const idx = goal.targets.findIndex((t) => t.targetId === action.payload);
        if (idx === -1 || goal.targets[idx].disabled) return goal;
        const newTargets = goal.targets.slice();
        newTargets[idx] = { ...newTargets[idx], checked: !newTargets[idx].checked };
        return { ...goal, targets: newTargets };
      });
    case SelectProgrammingActionKind.INITIALIZE:
      return createInitialState(action.goals);
    default:
      return state;
  }
}

function createInitialState(goals?: EhrGoal[] | null): SelectProgrammingState {
  const haveCheckedPreviously = goals?.some((g) => g.reportGoalCreated);
  return (goals ?? []).map((goal) => {
    const goalChecked = haveCheckedPreviously ? goal.reportGoalCreated : true;
    return {
      ...goal,
      checked: goalChecked,
      disabled: false,
      expanded: goalChecked,
      ehrTargets: undefined,
      targets: (goal.ehrTargets ?? []).filter(nonNullable).map((target) => ({
        ...target,
        checked: !target.reportTargetExcluded,
        disabled: false,
      })),
    };
  });
}

export function useSelectProgramming(goals?: GetReassessmentEhrGoalsQuery["reassessmentEhrGoals"] | null) {
  const [state, dispatch] = useReducer(reducer, createInitialState(goals?.filter((g): g is EhrGoal => g !== null)));
  useEffect(() => {
    const filtered = goals?.filter((g): g is EhrGoal => g !== null);
    if (filtered?.length) {
      dispatch({ type: SelectProgrammingActionKind.INITIALIZE, goals: filtered });
    }
  }, [goals]);
  return {
    state,
    toggleGoals: (ids: string[], checked: boolean, { cascadeTargets = false }: { cascadeTargets?: boolean } = {}) =>
      dispatch({ type: SelectProgrammingActionKind.TOGGLE_GOAL, payload: ids, checked, cascadeTargets }),
    toggleTarget: (id: string) => dispatch({ type: SelectProgrammingActionKind.TOGGLE_TARGET, payload: id }),
    toggleExpandGoal: (id: string) =>
      dispatch({
        type: SelectProgrammingActionKind.TOGGLE_EXPAND_GOAL,
        payload: id,
      }),
  };
}
